import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Briefcase, Search, Save, FileText, AlertTriangle, HelpCircle, Euro, ThumbsUp, ThumbsDown, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { BedrijfsafsprakenMelding } from "@/components/BedrijfsafsprakenMelding";
import { TerugNaarVraag } from "@/components/TerugNaarVraag";
import { PageHeader } from "@/pages/Kennisbank";
import { toast } from "sonner";

const Functiecheck = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [functienaam, setFunctienaam] = useState("");
  const [beschrijving, setBeschrijving] = useState("");
  const [afdeling, setAfdeling] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkId, setCheckId] = useState(null);
  const [viaMenu, setViaMenu] = useState(false);
  const [feedbackVerstuurd, setFeedbackVerstuurd] = useState(false);
  const [toonOnderbouwing, setToonOnderbouwing] = useState(false);

  useEffect(() => {
    const s = location.state;
    if (s) {
      if (s.functienaam) setFunctienaam(s.functienaam);
      if (s.beschrijving) setBeschrijving(s.beschrijving);
      if (s.afdeling) setAfdeling(s.afdeling);
    }
  }, [location.state]);

  const check = async () => {
    setBusy(true);
    try {
      const res = await api.post("/functiecheck", { functienaam, beschrijving, afdeling });
      setResult(res.data);
      setCheckId(crypto.randomUUID());
      setViaMenu(false);
      setFeedbackVerstuurd(false);
      setToonOnderbouwing(false);
    } finally { setBusy(false); }
  };

  const resolveWithChoice = async (optieId) => {
    setBusy(true);
    try {
      const res = await api.post("/functiecheck", { functienaam, beschrijving, afdeling, gekozen_functie: optieId });
      setResult(res.data);
      setCheckId(crypto.randomUUID());
      setViaMenu(true);
      setFeedbackVerstuurd(false);
      setToonOnderbouwing(false);
    } finally { setBusy(false); }
  };

  const stuurFeedback = (type) => {
    setFeedbackVerstuurd(true);
    api.post("/functie-feedback", {
      check_id: checkId,
      invoer: { functienaam, beschrijving, afdeling },
      uitkomst: {
        status: result.status,
        functiegroep: result.functiegroep != null ? String(result.functiegroep) : null,
        referentiefunctie: result.referentiefunctie || null,
        vertrouwensniveau: result.vertrouwensniveau || null,
        score: typeof result.score === "number" ? result.score : null,
        via_keuzemenu: viaMenu,
        uitleg_getoond: !!result.orba_uitleg,
      },
      feedback: type,
    }).catch(() => {});
  };

  const save = async () => {
    await api.post("/checks/save", {
      type_check: "functiecheck",
      invoerwaarden: { functienaam, beschrijving, afdeling },
      uitkomst: result,
    });
    toast.success("Functiecheck opgeslagen in je persoonlijke omgeving.");
  };

  return (
    <div className="animate-fade-up">
      <TerugNaarVraag />
      <PageHeader title="Functiecheck" icon={Briefcase}
        accent={{ chip: "bg-amber-500", bar: "from-amber-400 to-amber-600", text: "text-amber-600" }}
        subtitle="Krijg een indicatie van de mogelijke functiegroep op basis van je werkzaamheden en het functiehandboek. De uitkomst is uitsluitend een indicatie." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
          <div>
            <Label>Functienaam (indien bekend)</Label>
            <Input data-testid="functie-naam" value={functienaam} onChange={(e) => setFunctienaam(e.target.value)} placeholder="Bijv. Productiemedewerker" />
          </div>
          <div>
            <Label>Beschrijving van werkzaamheden</Label>
            <Textarea data-testid="functie-beschrijving" value={beschrijving} onChange={(e) => setBeschrijving(e.target.value)}
              placeholder="Beschrijf wat je dagelijks doet, bijv. 'Ik bedien de heftruck, verzamel orders en houd voorraad bij.'"
              className="min-h-[120px] border-slate-300 resize-none" />
          </div>
          <div>
            <Label>Afdeling / type werk (optioneel)</Label>
            <Input data-testid="functie-afdeling" value={afdeling} onChange={(e) => setAfdeling(e.target.value)} placeholder="Bijv. Logistiek, Productie, Techniek" />
          </div>
          <Button data-testid="functie-check-btn" onClick={check} disabled={busy} className="w-full bg-slate-900">
            <Search className="w-4 h-4 mr-2" /> {busy ? "Analyseren..." : "Functie controleren"}
          </Button>
        </div>

        <div>
          {result ? (
            result.status === "verduidelijking_nodig" ? (
              <div data-testid="functie-verduidelijking" className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 animate-fade-up">
                <div className="flex items-start gap-2 text-slate-700">
                  <HelpCircle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
                  <p className="text-sm">{result.melding}</p>
                </div>
                {result.naam_conflict && (
                  <div data-testid="functie-naam-conflict" className="bg-orange-50 border border-orange-200 text-orange-900 rounded-md p-3 text-sm flex gap-2 items-start">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{result.naam_conflict}</span>
                  </div>
                )}
                <p data-testid="functie-vraag" className="font-heading text-lg font-bold text-slate-900">{result.vraag}</p>
                <div className="space-y-2">
                  {result.opties.map((opt) => (
                    <button
                      key={opt.id}
                      data-testid={`functie-optie-${opt.id}`}
                      onClick={() => resolveWithChoice(opt.id)}
                      disabled={busy}
                      className="w-full text-left border border-slate-300 hover:border-slate-900 hover:bg-slate-50 rounded-md p-3 transition-colors disabled:opacity-50"
                    >
                      <span className="text-sm text-slate-800">{opt.label}</span>
                      <span className="block text-xs text-slate-500 mt-1">
                        {opt.functie}{opt.familie ? ` · ${opt.familie}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-sm flex gap-2 items-start">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{result.disclaimer}</span>
                </div>
              </div>
            ) : result.success ? (
              <div data-testid="functie-result" className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 animate-fade-up">
                <div className="flex flex-wrap gap-2">
                  <StatusBadge status={result.status} />
                </div>
                {result.naam_conflict && (
                  <div data-testid="functie-naam-conflict" className="bg-orange-50 border border-orange-200 text-orange-900 rounded-md p-3 text-sm flex gap-2 items-start">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{result.naam_conflict}</span>
                  </div>
                )}
                <div className="text-center py-2">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Mogelijke functiegroep</p>
                  <p data-testid="functie-groep" className="font-heading text-4xl font-bold text-slate-900 mt-1">Groep {result.functiegroep}</p>
                  <p className="text-sm text-slate-500">{result.schaalindicatie}</p>
                  {result.referentiefunctie && (
                    <p className="text-xs text-slate-500 mt-1">Dichtstbijzijnde referentiefunctie: <span className="font-medium text-slate-700">{result.referentiefunctie}</span>{result.familie ? ` · ${result.familie}` : ""}</p>
                  )}
                  {result.referentiefunctie && (
                    <button data-testid="functie-naar-handboek-link"
                      onClick={() => navigate(result.orba_uitleg?.onderbouwing?.functiecode
                        ? `/functiehandboek?functie=${result.orba_uitleg.onderbouwing.functiecode}`
                        : `/functiehandboek?zoek=${encodeURIComponent(result.referentiefunctie)}`)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#8a5a20] underline underline-offset-2 hover:text-[#B9772B]">
                      <BookOpen className="w-3.5 h-3.5" /> Bekijk deze functie in het Functiehandboek
                    </button>
                  )}
                </div>
                <div className="border-l-4 border-slate-900 pl-4 py-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Relevante beschrijving uit functiehandboek</p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed">{result.beschrijving}</p>
                </div>
                {result.orba_uitleg && (
                  <div data-testid="functie-orba-uitleg" className="bg-sky-50 border border-sky-200 rounded-md p-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-sky-700">Waarom deze indicatie?</p>
                    <p className="text-sm text-slate-800 leading-relaxed">{result.orba_uitleg.werkvloertaal}</p>
                    {result.orba_uitleg.kenmerken?.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-slate-500">Dit woog mee:</p>
                        {result.orba_uitleg.kenmerken.map((k) => (
                          <p key={k.naam} className="text-sm text-slate-700"><span className="font-semibold text-slate-900">{k.naam}</span> — {k.toelichting}</p>
                        ))}
                      </div>
                    )}
                    {result.orba_uitleg.onderbouwing && (
                      <div>
                        <button data-testid="orba-onderbouwing-toggle" onClick={() => setToonOnderbouwing(!toonOnderbouwing)}
                          className="text-sm font-medium text-sky-700 hover:text-sky-900 underline underline-offset-2">
                          {toonOnderbouwing ? "Verberg de onderbouwing" : "Toon de onderbouwing"}
                        </button>
                        {toonOnderbouwing && (
                          <div data-testid="orba-onderbouwing" className="mt-2 bg-white border border-slate-200 rounded-md p-3 text-sm text-slate-700 space-y-2">
                            <p><span className="font-semibold">Officiële onderbouwing:</span> {result.orba_uitleg.onderbouwing.referentiefunctie} · Functiegroep {result.orba_uitleg.onderbouwing.functiegroep}{result.orba_uitleg.onderbouwing.functiecode ? ` · Functiecode ${result.orba_uitleg.onderbouwing.functiecode}` : ""}</p>
                            <p>{result.orba_uitleg.onderbouwing.methode}</p>
                            <p className="text-xs text-slate-500">{result.orba_uitleg.onderbouwing.bron}</p>
                            <p className="text-xs text-slate-500">{result.orba_uitleg.onderbouwing.let_op}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div data-testid="functie-formulering" className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-800">{result.verplichte_formulering}</div>
                {result.functiegroep && (
                  <Button data-testid="naar-salarischeck-btn"
                    onClick={() => navigate("/salaris", { state: { functiegroep: result.functiegroep, van: location.state?.van } })}
                    className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Euro className="w-4 h-4 mr-2" /> Bekijk de bijbehorende salarisschaal
                  </Button>
                )}
                <div data-testid="functie-disclaimer" className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-sm flex gap-2 items-start">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{result.disclaimer}</span>
                </div>
                <BedrijfsafsprakenMelding />
                <p className="text-xs text-slate-500 border-l-4 border-slate-200 pl-3 flex items-start gap-1.5">
                  <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Gebruikte versie: {result.functiehandboek_versie}
                </p>
                <Button data-testid="functie-save-btn" onClick={save} variant="outline"
                  className="w-full bg-white border-2 border-[#B9772B] text-[#4A3122] hover:bg-[#FBF3E3] hover:text-[#4A3122] focus-visible:ring-2 focus-visible:ring-[#B9772B] focus-visible:ring-offset-2">
                  <Save className="w-4 h-4 mr-2" /> Bewaar deze check
                </Button>
                {(result.status === "groen" || result.status === "geel") && (
                  <div className="border-t border-slate-100 pt-4">
                    {feedbackVerstuurd ? (
                      <p data-testid="functie-feedback-bedankt" className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                        Bedankt voor je feedback! Deze wordt alleen gebruikt voor verbetering van de app.
                      </p>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Klopt deze indicatie volgens jou?</p>
                        <div className="grid grid-cols-3 gap-2">
                          <button data-testid="functie-feedback-klopt" onClick={() => stuurFeedback("klopt")}
                            className="flex items-center justify-center gap-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-md py-2 text-sm font-medium transition-colors">
                            <ThumbsUp className="w-4 h-4" /> Klopt
                          </button>
                          <button data-testid="functie-feedback-klopt-niet" onClick={() => stuurFeedback("klopt_niet")}
                            className="flex items-center justify-center gap-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-md py-2 text-sm font-medium transition-colors">
                            <ThumbsDown className="w-4 h-4" /> Klopt niet
                          </button>
                          <button data-testid="functie-feedback-weet-niet" onClick={() => stuurFeedback("weet_niet")}
                            className="flex items-center justify-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-md py-2 text-sm font-medium transition-colors">
                            <HelpCircle className="w-4 h-4" /> Weet ik niet
                          </button>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1.5">Alleen voor verbetering van de app; wordt anoniem opgeslagen.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="functie-error" className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800 space-y-2">
                <StatusBadge status={result.status} />
                <p className="font-medium">{result.melding}</p>
                <p className="text-sm">{result.detail}</p>
                <p className="text-xs">{result.disclaimer}</p>
              </div>
            )
          ) : (
            <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400 h-full flex items-center justify-center">
              Beschrijf je werkzaamheden voor een indicatieve functiegroep.
            </div>
          )}
        </div>
      </div>
      <TerugNaarVraag onderaan />
    </div>
  );
};

export default Functiecheck;
